import { Pencil } from '../legos/pencilTypes';
import { Deposit } from '../legos/depositTypes';

// Type of events we monitor and send over Websockets
export enum EventType {
  DESKING_LOCKED = 'DESKING_LOCKED',
  DESKING_UNLOCKED = 'DESKING_UNLOCKED',
  DEAL_CREATED_OR_UPDATED = 'DEAL_CREATED_OR_UPDATED',
  DEAL_PENCIL_UPDATED = 'DEAL_PENCIL_UPDATED',
  PENCIL_SENT_TO_SALESPERSON = 'PENCIL_SENT_TO_SALESPERSON',
  CHAT_ANSWERED = 'CHAT_ANSWERED',
  DEAL_FINANCIALS_OUTSIDE_PENCIL_UPDATED = 'DEAL_FINANCIALS_OUTSIDE_PENCIL_UPDATED',
  DEAL_FINANCE_APPLICATION_SENT = 'DEAL_FINANCE_APPLICATION_SENT',
  DEAL_FINANCE_APPLICATION_RESET = 'DEAL_FINANCE_APPLICATION_RESET',
  DEAL_FINANCE_RESPONSE_RECEIVED = 'DEAL_FINANCE_RESPONSE_RECEIVED',
  FINANCE_RESPONSES_AVAILABLE_FOR_DEALER = 'FINANCE_RESPONSES_AVAILABLE_FOR_DEALER',
  DEAL_FINANCE_APPLICATION_RIC_SIGNED = 'DEAL_FINANCE_APPLICATION_RIC_SIGNED',
  DEAL_DATA_UPDATED = 'DEAL_DATA_UPDATED',
  DEAL_CHAT_SENT = 'DEAL_CHAT_SENT',
  DEAL_ERROR_FORWARD = 'DEAL_ERROR_FORWARD',
  LOSS_EXPRESS_PAYOFF_UPDATED = 'LOSS_EXPRESS_PAYOFF_UPDATED',
  DESKING_UPDATED = 'DESKING_UPDATED',
  DEAL_FOR_DESKING_UPDATED = 'DEAL_FOR_DESKING_UPDATED',
  WAITING_FOR_OFFERS = 'WAITING_FOR_OFFERS',
  DEAL_DEPOSIT_UPDATED = 'DEAL_DEPOSIT_UPDATED',
  OFFER_UPDATED_BY_NEW_INCENTIVES = 'OFFER_UPDATED_BY_NEW_INCENTIVES',
}

// Tell backend if we want to subscribe or unsubscribe an event
export enum WebsocketCommandType {
  SUBSCRIBE = 'SUBSCRIBE',
  UNSUBSCRIBE = 'UNSUBSCRIBE',
}

export enum DealPencilUpdateSource {
  WEB = 'web',
  CHAIRMAN = 'chairman',
  PAPYRUS = 'papyrus',
  ONLINE = 'online',
}

// When client send a command to backend this is the payload format
export interface WebsocketCommandMessage {
  command: WebsocketCommandType;
  // What kind of events client wants to be notified of
  websocketEventFilter: WebsocketEventFilter;
}

// All WebsocketEventFilters must implement this interface
export interface WebsocketEventFilter {
  eventType: EventType;
}

// All WebsocketEvents must implement this interface
export interface WebsocketEvent {
  eventType: EventType;
}

// WebsocketEventFilter
// Sent by client to narrow down which events with those properties it is interesting in listening back
export interface DealCreatedOrUpdatedWebsocketEventFilter
  extends WebsocketEventFilter {
  dealerId: string;
  eventType: EventType.DEAL_CREATED_OR_UPDATED;
}

export interface ChatAnsweredWebsocketEventFilter extends WebsocketEventFilter {
  dealId: string;
  eventType: EventType.CHAT_ANSWERED;
}

export interface DealPencilUpdatedWebsocketEventFilter
  extends WebsocketEventFilter {
  eventType: EventType.DEAL_PENCIL_UPDATED;
  dealId?: string;
  dealerId?: string;
}

export interface SendPencilSentToSalesPersonWebsocketEventFilter
  extends WebsocketEventFilter {
  eventType: EventType.PENCIL_SENT_TO_SALESPERSON;
  dealId?: string;
}

export interface DealFinancialsOutsidePencilUpdatedWebsocketEventFilter
  extends WebsocketEventFilter {
  eventType: EventType.DEAL_FINANCIALS_OUTSIDE_PENCIL_UPDATED;
  dealId: string;
}

export interface LossExpressPayoffUpdatedWebsocketEventFilter
  extends WebsocketEventFilter {
  dealId: string;
  eventType: EventType.LOSS_EXPRESS_PAYOFF_UPDATED;
}

// WebsocketEvent
// Events that are published by MqttClient so it can be picked up by servers and sent to clients
export interface DealCreatedOrUpdatedWebsocketEvent extends WebsocketEvent {
  dealerId: string;
  dealId: string;
  eventType: EventType.DEAL_CREATED_OR_UPDATED;
}

export interface ChatAnsweredWebsocketEvent extends WebsocketEvent {
  id: string;
  dealId: string;
  eventType: EventType.CHAT_ANSWERED;
}

export interface DealPencilUpdatedWebsocketEvent extends WebsocketEvent {
  eventType: EventType.DEAL_PENCIL_UPDATED;
  dealId: string;
  dealerId: string;
  pencilId?: string;
  source?: DealPencilUpdateSource;
  pencil?: Partial<Pencil>;
}

export interface SendPencilSentToSalesPersonWebsocketEvent
  extends WebsocketEvent {
  eventType: EventType.PENCIL_SENT_TO_SALESPERSON;
  dealId: string;
}

export interface DealFinancialsOutsidePencilUpdatedEvent
  extends WebsocketEvent {
  eventType: EventType.DEAL_FINANCIALS_OUTSIDE_PENCIL_UPDATED;
  dealId: string;
  update: {
    downPayment?: number | null;
  };
}

export interface DealFinanceApplicationSentEvent extends WebsocketEvent {
  eventType: EventType.DEAL_FINANCE_APPLICATION_SENT;
  dealId: string;
  dealerId: string;
}

export interface DealFinanceApplicationResetEvent extends WebsocketEvent {
  eventType: EventType.DEAL_FINANCE_APPLICATION_RESET;
  dealId: string;
}

export interface DealFinanceResponseReceivedEvent extends WebsocketEvent {
  eventType: EventType.DEAL_FINANCE_RESPONSE_RECEIVED;
  dealId: string;
}

export interface FinanceResponsesAvailableForDealerEvent
  extends WebsocketEvent {
  eventType: EventType.FINANCE_RESPONSES_AVAILABLE_FOR_DEALER;
  dealerId: string;
}

export interface DealFinanceApplicationRICSignedEvent extends WebsocketEvent {
  eventType: EventType.DEAL_FINANCE_APPLICATION_RIC_SIGNED;
  dealId: string;
}

export interface DealDataUpdatedWebsocketEvent extends WebsocketEvent {
  eventType: EventType.DEAL_DATA_UPDATED;
  dealId: string;
}

export interface DealErrorForwardWebsocketEvent extends WebsocketEvent {
  eventType: EventType.DEAL_ERROR_FORWARD;
  userId: number;
  dealId: string;
  dealError?: {
    action: string;
    crmType: string;
    crmVisitID: string;
    errorMessage?: string;
  };
}

export interface LossExpressPayoffUpdatedWebsocketEvent extends WebsocketEvent {
  dealId: string;
  eventType: EventType.LOSS_EXPRESS_PAYOFF_UPDATED;
}

export interface DeskingUpdatedWebsocketEvent extends WebsocketEvent {
  eventType: EventType.DESKING_UPDATED;
  dealId: string;
  carId: string;
}

export interface DealForDeskingUpdatedWebsocketEvent extends WebsocketEvent {
  eventType: EventType.DEAL_FOR_DESKING_UPDATED;
  dealId: string;
}

export interface WaitingForOffersWebsocketEvent extends WebsocketEvent {
  eventType: EventType.WAITING_FOR_OFFERS;
  dealId: string;
}

export interface DealDepositUpdatedWebsocketEvent extends WebsocketEvent {
  eventType: EventType.DEAL_DEPOSIT_UPDATED;
  dealId: string;
  deposit: Deposit;
}

export interface OfferUpdatedByNewIncentivesEvent extends WebsocketEvent {
  eventType: EventType.OFFER_UPDATED_BY_NEW_INCENTIVES;
  dealId: string;
  deskingId: string;
  updatedProperties: string[];
}

export interface DeskingLockedWebsocketEvent extends WebsocketEvent {
  eventType: EventType.DESKING_LOCKED;
  dealId: string;
  deskingId: string;
  isUniqueLock: boolean;
  carId?: string;
  deskingLockedAt: Date;
}

export interface DeskingUnlockedWebsocketEvent extends WebsocketEvent {
  eventType: EventType.DESKING_UNLOCKED;
  dealId: string;
  deskingId: string;
  carId?: string;
}
